import { useEffect, useState } from 'react'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { fetchMesaForQR } from '../../../api/qrApi'
import { PaymentSection } from './PaymentSection'
import MesaLandingPage from '../../payments/pages/MesaLandingPage'

interface TableViewProps {
  mesaId: string
}

/**
 * QR View - Displays mesa data for customers
 * 
 * ✅ QR ONLY calls ONE endpoint: /qrApi/mesa/:mesaId
 * ❌ QR NEVER talks to Firestore or POS directly
 * ❌ QR NEVER resolves tickets or computes payments
 * 
 * All data comes from the mesa document (synced by admin)
 * 
 * QR UI RULES:
 * ❌ No Firestore reads
 * ❌ No POS calls
 * ❌ No listeners
 * ❌ No derived totals
 * ✅ Render-only
 * ✅ One API
 * ✅ One state object
 */
export function TableView({ mesaId }: TableViewProps) {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const ticket = searchParams.get('ticket') ?? undefined
  const [mesa, setMesa] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const routeState = (location.state as {
    showPaymentSection?: boolean
    initialPaymentMode?: 'full' | 'select' | 'split' | 'fixed'
  }) || {}
  const [showLanding, setShowLanding] = useState(() => !routeState.showPaymentSection)
  const [initialPaymentMode, setInitialPaymentMode] = useState<'full' | 'select' | 'split' | 'fixed'>(
    () => routeState.initialPaymentMode || 'full'
  )
  const [showSplitSheet, setShowSplitSheet] = useState(false)

  useEffect(() => {
    if (routeState.showPaymentSection) {
      setShowLanding(false)
      setInitialPaymentMode(routeState.initialPaymentMode || 'full')
    }
  }, [routeState.showPaymentSection, routeState.initialPaymentMode])

  useEffect(() => {
    let cancelled = false
    async function loadMesa() {
      try {
        if (!mesaId) {
          throw new Error('mesaId is required')
        }

        const data = await fetchMesaForQR(mesaId, ticket)
        if (!cancelled) setMesa(data)
      } catch (e: any) {
        if (cancelled) return
        console.error('Failed to load mesa:', e)
        setError(e.message || 'No se pudo cargar la cuenta')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadMesa()
    return () => { cancelled = true }
  }, [mesaId, ticket])

  // Re-check mesa state periodically so that if mesa is closed, UI blocks without reload
  useEffect(() => {
    if (!mesaId || !mesa?.restaurantId || loading || error) return
    const interval = setInterval(async () => {
      try {
        const data = await fetchMesaForQR(mesaId, ticket)
        setMesa(data)
      } catch (e: any) {
        if (e?.message === 'Mesa no disponible') {
          setMesa((prev: any) => (prev ? { ...prev, estado: 'cerrada', activeSessionId: null } : null))
        }
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [mesaId, ticket, loading, error, mesa?.restaurantId])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando datos del ticket…</p>
        </div>
      </div>
    )
  }

  if (error) {
    if (error === 'Mesa no disponible') {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-gray-50">
          <h2 className="text-2xl font-semibold text-black">Cuenta cerrada</h2>
          <p className="text-gray-500 mt-2">Esta cuenta ya fue cerrada.</p>
          <p className="text-gray-400 mt-1 text-sm">
            Si deseas pagar, solicita al mesero abrir la cuenta nuevamente.
          </p>
          <p className="text-gray-500 mt-3 text-sm">{"\uD83D\uDD12"} Sesión cerrada</p>
        </div>
      )
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    )
  }

  if (!mesa) return null

  const cuentaTotal = Number(mesa.montoTotalConImpuestos ?? mesa.montoTotal ?? 0)
  const isBlocked =
    mesa.estado !== 'abierta' ||
    !mesa.activeSessionId ||
    cuentaTotal <= 0.01
  if (isBlocked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-gray-50">
        <h2 className="text-2xl font-semibold text-black">Cuenta cerrada</h2>
        <p className="text-gray-500 mt-2">Esta cuenta ya fue cerrada.</p>
        <p className="text-gray-400 mt-1 text-sm">
          Si deseas pagar, solicita al mesero abrir la cuenta nuevamente.
        </p>
        <p className="text-gray-500 mt-3 text-sm">{"\uD83D\uDD12"} Sesión cerrada</p>
      </div>
    )
  }

  const totalAmount =
    (mesa.montoTotalConImpuestos ?? mesa.restante ?? 0) ||
    ((mesa.montoTotal ?? 0) * (1 + (mesa.taxRate ?? 18) / 100))
  const restaurantName = mesa.restaurantName || 'Restaurant'
  const restaurantLogoUrl = mesa.restaurantLogoUrl ?? null
  const isManual = mesa.integrationMode === 'manual'

  // Manual mode totals
  const montoTotalConImpuestos = Number(mesa.montoTotalConImpuestos ?? 0)
  const montoPagado = Number(mesa.montoPagado ?? 0)
  const restante = Number(mesa.restante ?? 0)
  const moneda = mesa.moneda ?? 'DOP'

  if (showLanding) {
    return (
      <MesaLandingPage
        restaurantName={restaurantName}
        restaurantLogoUrl={restaurantLogoUrl}
        mesaNumber={String(mesa.numeroMesa ?? '')}
        totalAmount={totalAmount}
        isManual={isManual}
        montoTotalConImpuestos={montoTotalConImpuestos}
        montoPagado={montoPagado}
        restante={restante}
        mesaId={String(mesa.mesaId ?? mesaId ?? '')}
        restaurantId={String(mesa.restaurantId ?? '')}
        moneda={moneda}
        onPayFull={() => {
          if (isManual) {
            // Manual: no POS items; go straight to tip selection using remaining amount.
            navigate('/tip-selection', {
              state: {
                mesaId: String(mesa.mesaId ?? mesaId ?? ''),
                restaurantId: String(mesa.restaurantId ?? ''),
                waiterName: mesa.numeroMesa || 'tu mesero',
                staffId: mesa.staffId,
                amount: restante,
                moneda: moneda ?? 'DOP',
              },
            })
            return
          }

          setInitialPaymentMode('full')
          setShowLanding(false)
        }}
        onSplitBill={() => setShowSplitSheet(true)}
        showSplitSheet={showSplitSheet}
        onCloseSplitSheet={() => setShowSplitSheet(false)}
        onSplitOption={(mode) => {
          setShowSplitSheet(false)
          setInitialPaymentMode(mode === 'fixed' ? (isManual ? 'fixed' : 'split') : mode)
          setShowLanding(false)
        }}
      />
    )
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 w-full">
      <PaymentSection
        mesa={mesa}
        initialPaymentMode={initialPaymentMode}
        onBack={
          initialPaymentMode === 'full'
            ? () => setShowLanding(true)
            : () => {
                setShowLanding(true)
                setShowSplitSheet(true)
              }
        }
      />
    </div>
  )
}
